import { captureException } from "@sentry/react";
import { useEffect } from "react";
import { isRouteErrorResponse, useInRouterContext, useRouteError } from "react-router";

import Logo403 from "@/assets/images/403.svg";
import Logo404 from "@/assets/images/404.svg";
import Logo500 from "@/assets/images/500.svg";
import Logo503 from "@/assets/images/503.svg";
import { Button } from "@/design-system/Button";
import { SUPPORT_EMAIL } from "@/helpers/constants";
import { isHttpError } from "@/helpers/Network/errors";
import { Trans, useIsTranslationsAvailable, useTranslation } from "@/translations";

import { ConnectionFailurePage } from "./ConnectionFailurePage";
import { ErrorPageInner } from "./ErrorPageInner";

interface ErrorPageProps {
  status?: number;
  error?: unknown;
  extraInfo?: string;
}

interface ErrorInnerPageContentProps {
  content: {
    title: string;
    status: string;
    sessionId?: string;
    solutions: (React.ReactNode | string)[];
    image: string;
    extraInfo?: string;
  };
}

export function ErrorPage({ status, error, extraInfo }: ErrorPageProps): React.ReactNode {
  const ready = useIsTranslationsAvailable();
  const isInRouter = useInRouterContext();

  if (!ready) {
    return <ConnectionFailurePage />;
  }

  return isInRouter ? (
    <RouterErrorContent error={error} status={status} extraInfo={extraInfo} />
  ) : (
    <ErrorContent error={error} status={status} extraInfo={extraInfo} />
  );
}

function RouterErrorContent({
  error,
  status,
  extraInfo,
}: {
  error?: unknown;
  status?: number;
  extraInfo?: string;
}): React.ReactNode {
  const routeError = useRouteError();
  const resolvedError = error ?? routeError;

  return <ErrorContent error={resolvedError} status={status} extraInfo={extraInfo} />;
}

function ErrorContent({ error, status, extraInfo }: ErrorPageProps): React.ReactNode {
  const resolvedStatus = resolveErrorStatus(status, error);

  return (
    <ErrorLogger error={error} status={resolvedStatus}>
      {getErrorPage(resolvedStatus, extraInfo)}
    </ErrorLogger>
  );
}

function resolveErrorStatus(status: number | undefined, error: unknown): number | undefined {
  if (status != null) {
    return status;
  }

  if (error instanceof Response || isRouteErrorResponse(error) || isHttpError(error)) {
    return error.status;
  }

  if (typeof error === "number" && error > 0 && error < 600) {
    return error;
  }

  return undefined;
}

function ErrorLogger({ error, status, children }: { error?: unknown; status?: number; children: React.ReactNode }) {
  let resolvedStatus = status;
  if (!status && isHttpError(error)) {
    resolvedStatus = error.status;
  }

  useEffect(() => {
    // Report failures when the error page becomes visible, excluding ordinary missing routes.
    const isApiError =
      !isRouteErrorResponse(error) && (isHttpError(error) || (error instanceof Response && !!error.url));
    if (resolvedStatus === 404 && !isApiError) {
      return;
    }

    if (error) {
      captureException(error, {
        level: resolvedStatus === 404 || resolvedStatus === 403 ? "warning" : "error",
      });
    } else if (resolvedStatus) {
      captureException(new Error(`Page failed with status ${resolvedStatus}`), {
        level: resolvedStatus === 404 || resolvedStatus === 403 ? "warning" : "error",
        fingerprint: ["page", String(resolvedStatus)],
        tags: { http_status: resolvedStatus },
      });
    }
  }, [error, resolvedStatus]);

  return children;
}

function getErrorPage(status?: number, extraInfo: string | undefined = undefined) {
  switch (status) {
    case 404:
      return <NotFoundPage extraInfo={extraInfo} />;
    case 403:
      return <ForbiddenPage extraInfo={extraInfo} />;
    case 500:
      return <InternalServerErrorPage extraInfo={extraInfo} />;
    case 503:
      return <GenericErrorPage status="503" extraInfo={extraInfo} />;
    default:
      return <GenericErrorPage status="Unknown" extraInfo={extraInfo} />;
  }
}

function GenericErrorPage({ status, extraInfo }: { status: string; extraInfo: string | undefined }) {
  const { t } = useTranslation();
  const solutions: (React.ReactNode | string)[] = [];

  solutions.push(
    <Trans
      i18nKey="page.error.generic.solutions.refresh"
      values={{ page: t("page.error.generic.refresh") }}
      components={{
        anchor: (content) => (
          <Button variant="ghostPrimary" onClick={() => location.reload()}>
            {content}
          </Button>
        ),
      }}
    />,
  );

  solutions.push(t("page.error.generic.solutions.later"));

  solutions.push(
    <Trans
      i18nKey="page.error.generic.solutions.contact"
      components={{
        email: (
          <Button variant="ghostPrimary" type="link" href={`mailto:${SUPPORT_EMAIL}`} isExternal>
            {SUPPORT_EMAIL}
          </Button>
        ),
      }}
    />,
  );

  return (
    <ErrorPageTemplate
      content={{
        title: t("page.error.generic.sorry"),
        status: status,
        solutions,
        image: Logo503,
        extraInfo,
      }}
    />
  );
}

function InternalServerErrorPage({ extraInfo }: { extraInfo?: string }) {
  const { t } = useTranslation();
  const solutions: (React.ReactNode | string)[] = [];

  solutions.push(
    <Trans
      i18nKey="page.error.generic.solutions.refresh"
      values={{ page: t("page.error.generic.refresh") }}
      components={{
        anchor: (content) => (
          <Button variant="ghostPrimary" onClick={() => location.reload()}>
            {content}
          </Button>
        ),
      }}
    />,
  );

  solutions.push(t("page.error.generic.solutions.later"));

  solutions.push(
    <Trans
      i18nKey="page.error.generic.solutions.link"
      values={{ page: t("page.error.generic.solutions.link.name") }}
      components={{
        anchor: (content) => (
          <Button variant="ghostPrimary" type="link" href="/">
            {content}
          </Button>
        ),
      }}
    />,
  );

  solutions.push(
    <Trans
      i18nKey="page.error.server.solutions.contact"
      components={{
        email: (
          <Button variant="ghostPrimary" type="link" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </Button>
        ),
      }}
    />,
  );

  return (
    <ErrorPageTemplate
      content={{
        title: t("page.error.server.sorry"),
        status: "500",
        solutions,
        image: Logo500,
        extraInfo,
      }}
    />
  );
}

function ForbiddenPage({ extraInfo }: { extraInfo?: string }) {
  const { t } = useTranslation();
  const solutions: (React.ReactNode | string)[] = [];

  solutions.push(
    <Trans
      i18nKey="page.error.generic.solutions.link"
      values={{ page: t("page.error.generic.solutions.link.name") }}
      components={{
        anchor: (content) => (
          <Button variant="ghostPrimary" type="link" href="/">
            {content}
          </Button>
        ),
      }}
    />,
  );

  solutions.push(
    <Trans
      i18nKey="page.error.forbidden.solutions.contact"
      components={{
        email: (
          <Button variant="ghostPrimary" type="link" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </Button>
        ),
      }}
    />,
  );

  return (
    <ErrorPageTemplate
      content={{
        title: t("page.error.forbidden.sorry"),
        status: "403",
        solutions,
        image: Logo403,
        extraInfo,
      }}
    />
  );
}

function NotFoundPage({ extraInfo }: { extraInfo?: string }) {
  const { t } = useTranslation();
  const solutions: (React.ReactNode | string)[] = [];

  solutions.push(
    <Trans
      i18nKey="page.error.generic.solutions.link"
      values={{ page: t("page.error.generic.solutions.link.name") }}
      components={{
        anchor: (content) => (
          <Button variant="ghostPrimary" type="link" href="/">
            {content}
          </Button>
        ),
      }}
    />,
  );

  solutions.push(t("page.error.not-found.solutions.check"));

  return (
    <ErrorPageTemplate
      content={{
        title: t("page.error.not-found.sorry"),
        status: "404",
        solutions,
        image: Logo404,
        extraInfo,
      }}
    />
  );
}

function ErrorPageTemplate({ content }: ErrorInnerPageContentProps) {
  const { t } = useTranslation();

  return (
    <ErrorPageInner
      labels={{
        error: t("page.error.generic.code"),
        solutions: t("page.error.generic.solutions.title"),
      }}
      content={content}
    />
  );
}
